import { TranslationKey } from './../../../../types/translation/TranslationKey';
import React from 'react';
import { ProductMasterData } from '../../../../types/product_master_data/productMasterData';
import { TagRelation } from '../../../../types/tag/tagRelation';
import { t } from '../../../../types/translation/Translator';
import { ProductIcon } from '../../../icons/Product/ProductIcon';
import { BaseFilter, BaseFilterProps } from '../common/BaseFilter';
import { FilterStorageKey, FilterProps } from '../common/filter.util';
import { TagFilter } from '../TagFilter';
import { CustomFieldFilter } from '../CustomFieldFilter';
import { CustomField, CustomFieldEntityType } from '../../../../types/custom_field/customField';
import { ProductMasterDataFilter } from '../ProductMasterDataFilter';
import { itemsContainText } from '../../../../util/string.util';
import { EntityType } from '../../../../types/comment';
import { StockLocation } from '../../../../types/stock_location';
import { Bin, BinStatus } from '../../../../types/bin';
import { BinFilter } from '../BinFilter';
import { StockLocationFilter } from '../StockLocationFilter';
import { BinConstraint } from '../../../../types/bin_constraints/binConstraint';
import { Company } from '../../../../types/company';

export class BinConstraintFilter extends BaseFilter<BinConstraint> {
  productMasterData: Map<string, ProductMasterData>;
  bins: Map<string, Bin>;
  stockLocations: Map<string, StockLocation>;
  customFields: Map<string, CustomField>;
  tagRelations: Map<string, TagRelation>;
  binStatuses: Map<string, BinStatus>;
  company: Company;

  constructor(
    productMasterData: Map<string, ProductMasterData>,
    bins: Map<string, Bin>,
    stockLocations: Map<string, StockLocation>,
    tagRelations: Map<string, TagRelation>,
    customFields: Map<string, CustomField>,
    binStatuses: Map<string, BinStatus>,
    company: Company,
  ) {
    super(FilterStorageKey.BIN_CONSTRAINT);
    this.productMasterData = productMasterData;
    this.bins = bins;
    this.stockLocations = stockLocations;
    this.customFields = customFields;
    this.tagRelations = tagRelations;
    this.binStatuses = binStatuses;
    this.company = company;
  }

  filterOptions = () => [
    new StockLocationFilter(this.tagRelations, this.company),
    new ProductMasterDataFilter(this.customFields, this.tagRelations, this.company),
    new BinFilter(this.binStatuses, this.tagRelations, this.customFields, this.bins, this.company, this.stockLocations),
    new TagFilter(EntityType.pmd),
    ...CustomFieldFilter.for(CustomFieldEntityType.productMasterData, this.productMasterData, this.customFields),
  ];

  toLabel = () => {
    return t(TranslationKey.binConstraint).singular.label;
  };

  toCount(props: BaseFilterProps) {
    return props.filterProps.binConstraints?.size || 0;
  }

  toDropdownContent() {
    return <></>;
  }

  toInnerContent = () => {
    return <></>;
  };

  toIcon = () => {
    return <ProductIcon className={this.className} />;
  };

  search = (item: BinConstraint, query: string) => {
    const productName = this.productMasterData.get(item.pmdId)?.productName;
    const productNumber = this.productMasterData.get(item.pmdId)?.productNumber;
    const stockLocation = this.stockLocations.get(item.stockLocationId)?.name;
    const bin = this.bins.get(item.binId)?.name;
    return itemsContainText(query, [productName, productNumber, stockLocation, bin]);
  };

  filteredItems(items: BinConstraint[], query: string, filterProps: FilterProps): BinConstraint[] {
    return items.filter(item => {
      const pmd = this.productMasterData.get(item.pmdId);
      const bin = this.bins.get(item.binId);

      return [
        ProductMasterDataFilter.filter(filterProps, pmd, this.customFields, this.tagRelations),
        StockLocationFilter.filter(filterProps, this.stockLocations.get(item.stockLocationId)),
        BinFilter.filter(filterProps, bin, this.customFields),
        TagFilter.filterBy(filterProps, EntityType.pmd, this.tagRelations, pmd?.id),
        BinConstraintFilter.filter(filterProps, item, this.customFields),
        this.search(item, query),
      ].every(value => value === null || value);
    });
  }

  static filter(filterProps: FilterProps, item?: BinConstraint, customFields: Map<string, CustomField> = new Map()) {
    return true;
  }
}
